export enum AreaType {
  Pixels = "Pixels",
  PixelsFromCorners = "PixelsFromCorners",
  Percentage = "Percentage",
  PercentageFromCorners = "PercentageFromCorners",
}

export type Area = {
  type: AreaType;
  left: number;
  top: number;
  right: number;
  bottom: number;
};

export enum PhotoAlignment {
  None = "None",
  Start = "Start",
  Center = "Center",
  End = "End",
}

export interface Media {
  source: string | null;
}

export type Image = Media;

export interface MediaBuild {
  operations: Operation[];
}

export interface ImageBuild extends MediaBuild {
  image: Image;
}

export type Video = Media;
export type Audio = Media;

export interface VideoBuild extends MediaBuild {
  video: Video;
  previewSize?: number;
  extendedProperties?: Record<string, any>;
  sourceAudioVolume?: number; // 0.0 - 1.0, default: 1.0. Volume for the source video's own audio track.
}

export type ExtendedPropertiesDictionary = Record<string, any>;

export interface Operation {
  extendedProperties?: ExtendedPropertiesDictionary;
}

export interface OverlayOperation extends Operation {
  area: Area;
  backgroundColor?: string;
}

export interface ImageOperation extends OverlayOperation {
  image: Image;
  horizontalAlignment: PhotoAlignment;
  verticalAlignment: PhotoAlignment;
  canScaleUp: boolean;
  backgroundScale: number;
  removeImageBackground: boolean;
}

export interface TextOperation extends OverlayOperation {
  text: string;
  color: string;
  fontName: string;
  fontSize: number;
  sourceSize?: number; 
}

export interface AudioOperation extends Operation {
  audio: Audio;
  loop: boolean;
  volume?: number; // 0.0 - 1.0, default: 1.0
}

export interface TtsOperation extends Operation {
  tts: {
    text: string;
    voiceName: string;
    audioData?: string;
    duration?: number;
    volume?: number; // 0.0 - 1.0, default: 1.0
    _lastGenerated?: {
      text: string;
      voiceName: string;
    };
  };
}
export interface AiVideoOperation extends Operation {
  aiVideo: {
    filterId?: string;
    filterName: string;
    prompt: string;
    substitutions: Record<string, string>;
    videoData?: string;
    duration?: number;
    aspectRatio: string;
    previewUrl?: string;
    _lastGenerated?: {
      filterId?: string;
      prompt: string;
      substitutions: Record<string, string>;
      aspectRatio: string;
    };
  };
}

export enum MediaType {
  Unknown = 0,
  BuildContent = 1,
  SampleBackground = 2,
  SamplePhoto = 4,
  SamplePhotoWithoutBackground = 8,
  SampleStockPhoto = 16,
  Video = 128,
}

export const mimeTypeJpeg = "image/jpeg";
export const mimeTypePng = "image/png";
export const mimeTypeMp4 = "video/mp4";
export const mimeTypeAudioMp3 = "audio/mpeg";
export const mimeTypeAudioWav = "audio/wav";
export const mimeTypeAvi = "video/avi";
export const mimeTypeMov = "video/quicktime";
export const mimeTypeMkv = "video/x-matroska";
export const mimeTypeWebm = "video/webm";
export const mimeTypeAudioOgg = "audio/ogg";
export const mimeTypeAudioAac = "audio/aac";
export const mimeTypeAudioFlac = "audio/flac";
export const mimeTypeAudioM4a = "audio/mp4";
export const mimeTypeAudioWebm = "audio/webm";

export const allVideoMimeTypes = [mimeTypeMp4, mimeTypeAvi, mimeTypeMov, mimeTypeMkv, mimeTypeWebm];

// File extensions for video file picker (browser compatibility)
// Note: .mp4 is intentionally omitted as video/mp4 MIME type is already recognized by browsers
export const videoFileExtensions = ".avi,.mov,.mkv,.webm";

export const allAudioMimeTypes = [
  mimeTypeAudioMp3, 
  mimeTypeAudioWav, 
  mimeTypeAudioOgg, 
  mimeTypeAudioAac, 
  mimeTypeAudioFlac, 
  mimeTypeAudioM4a,
  mimeTypeAudioWebm
];

export const audioFileExtensions = ".mp3,.wav,.ogg,.aac,.flac,.m4a,.webm";

// All supported image MIME types for video creation
export const allImageMimeTypes = [mimeTypeJpeg, mimeTypePng];
export const imageFileExtensions = ".jpg,.jpeg,.png";

// Combined media types for New build dialog
export const allMediaMimeTypes = [...allVideoMimeTypes, ...allImageMimeTypes];
export const allMediaFileExtensions = `${videoFileExtensions},${imageFileExtensions}`;

export function isVideoMimeType(mimeType: string): boolean {
  return allVideoMimeTypes.includes(mimeType);
}

export function isImageMimeType(mimeType: string): boolean {
  return mimeType == mimeTypeJpeg || mimeType == mimeTypePng;
}

export function isAudioMimeType(mimeType: string): boolean {
  return allAudioMimeTypes.includes(mimeType);
}

export class CreateMediaRequest {
  type: MediaType;
  mimeType: string;
  content: string;

  constructor(type: MediaType, mimeType: string, content: string) {
    this.type = type;
    this.mimeType = mimeType;
    this.content = content;
  }
}

export interface UpdateMediaRequest {
  id: string;
  type: MediaType;
  mimeType: string;
  content: string;
}

export interface MediaItemBase {
  mimeType: string;
  content: string;
}

export interface MediaItem extends MediaItemBase {
  id: string;
  type: MediaType;
  previewId?: string | null;
  previewUrl?: string | null;
}

// Image build
export type ImageBuildSummaryResponse = {
  id: string;
  name: string;
  previewId: string | null;
  previewUrl: string | null;
  tags: string[];
};

export type ImageBuildDetailedResponse = ImageBuildSummaryResponse & ImageBuild;

export type CreateImageBuildRequest = {
  name: string;
  tags: string[];
  reservationId?: string;
} & ImageBuild;

export type UpdateImageBuildRequest = {
  id: string;
  name: string;
  tags: string[];
} & ImageBuild;

export type CreateImageBuildResponse = UpdateImageBuildRequest & {
  previewId: string | null;
  previewUrl: string | null;
};

export type UpdateImageBuildResponse = CreateImageBuildResponse;

// Video build

export type CreateVideoBuildRequest = {
  name: string;
  tags: string[];
  reservationId?: string;
  extendedProperties?: Record<string, any>;
  clientProvidesRenderedVideo?: boolean;
} & VideoBuild;

export type UpdateVideoBuildRequest = {
  id: string;
  name: string;
  tags: string[];
  extendedProperties?: Record<string, any>;
} & VideoBuild;

export type MediaBuildSummaryResponse = {
  id: string;
  name: string;
  previewId: string | null;
  previewUrl: string | null;
  tags: string[];
};

export type MediaBuildDetailedResponse = MediaBuildSummaryResponse & MediaBuild;

export type VideoBuildSummaryResponse = MediaBuildSummaryResponse & {
  reservationId?: string;
};

export type VideoBuildDetailedResponse = MediaBuildDetailedResponse &
  VideoBuild & {
    reservationId?: string;
  };

export type ConvertVideoRequest = {
  video:  {
    source: string;
  };
  targetSize?: number;
  reduction?: number;
  horizontalOffset?: number;
  verticalOffset?: number;
};

export type ConvertImageToVideoRequest = {
  image: { source: string };
  targetSize?: number;
  duration?: number;  // seconds, default 5
  reduction?: number;
  horizontalOffset?: number;
  verticalOffset?: number;
};

export interface AnalyzeVideoResponse {
  previewImage: string;  // data:image/jpeg;base64,...
  width: number;
  height: number;
  duration: number;
}

export interface SaveRenderedVideoToDeviceRequest {
  videoBuildId: string;
  renderedVideoSource: string;
}

export type DocumentSummaryResponse = {
  id: string;
  name: string;
  providerId: string;
  isGlobal: boolean;
};

export type UpdateDocumentRequest = Omit<
  DocumentSummaryResponse,
  "providerId"
> & {
  source: string;
};

export type CreateDocumentRequest = Omit<DocumentSummaryResponse, "id"> & {
  source: string;
};
